import { memo, useState } from 'react'
import { GS } from 'back-end-api'
import { useTranslation } from 'services/Translation'
import LoaderMini from 'components/common/loaders/mini'

import texts from './texts'
import { DEFAULT_TIME_FILTER, TimeFilterValue } from './helpers'
import { Area, Heading, SubHeading, TabsHeader, Tab, TabBody } from './Styles'
import Onboarding from './Onboarding'
import TimeFilter from './TimeFilter'
import FinancialMetrics from './Tabs/FinancialMetrics'
import ServiceMetrics from './Tabs/ServiceMetrics'
import ConsultDetails from './Tabs/ConsultDetails'
import Invoices from './Tabs/Invoices'

const TABS = {
  FINANCIAL: 'financial',
  SERVICE: 'service',
  CONSULT: 'consult',
  INVOICES: 'invoices',
} as const

type TabKey = typeof TABS[keyof typeof TABS]

// Wallet & Analytics dashboard area (tasks 15.2–15.5). Rendered by the
// DashboardLayout when the route matches ROUTES.WALLET.
const WalletContent = () => {
  const { t } = useTranslation(texts)
  const account = GS.useOwnAccount({ refreshInterval: 0 })

  const [activeTab, setActiveTab] = useState<TabKey>(TABS.FINANCIAL)
  const [filter, setFilter] = useState<TimeFilterValue>(DEFAULT_TIME_FILTER)

  const onboarded = Boolean(account?.data?.bank_details_set)

  // In-house (employed) doctors do not participate in the freelance wallet, so
  // the Wallet & Analytics area is hidden and inaccessible for them
  // (Requirements 4.1, 5.1). Written defensively so the area renders whenever
  // in_house_doctor is false or absent. Wait for the account to load before
  // hiding, so a non-in-house doctor's wallet is never momentarily blanked.
  const inHouse = Boolean(account?.data?.in_house_doctor)
  if (!account?.isLoading && inHouse) {
    return null
  }

  // The Invoices tab does not use the time filter; the three analytics tabs do.
  const showTimeFilter = activeTab !== TABS.INVOICES

  const renderTab = () => {
    switch (activeTab) {
      case TABS.FINANCIAL:
        return <FinancialMetrics filter={filter} onboarded={onboarded} />
      case TABS.SERVICE:
        return <ServiceMetrics filter={filter} />
      case TABS.CONSULT:
        return <ConsultDetails filter={filter} />
      case TABS.INVOICES:
        return <Invoices />
      default:
        return <FinancialMetrics filter={filter} onboarded={onboarded} />
    }
  }

  return (
    <Area data-cy="walletContent">
      <Heading>{t('title')}</Heading>
      <SubHeading>{t('subtitle')}</SubHeading>

      {/* While loading the account, avoid flashing the onboarding CTA. */}
      {account?.isLoading ? (
        <TabBody>
          <LoaderMini center />
        </TabBody>
      ) : (
        <>
          {/* Onboarding CTA + payouts-unavailable indicator (Requirement 1.5). */}
          {!onboarded && <Onboarding />}

          <TabsHeader>
            <Tab active={activeTab === TABS.FINANCIAL} onClick={() => setActiveTab(TABS.FINANCIAL)}>
              {t('financialMetrics')}
            </Tab>
            <Tab active={activeTab === TABS.SERVICE} onClick={() => setActiveTab(TABS.SERVICE)}>
              {t('serviceMetrics')}
            </Tab>
            <Tab active={activeTab === TABS.CONSULT} onClick={() => setActiveTab(TABS.CONSULT)}>
              {t('consultDetails')}
            </Tab>
            <Tab active={activeTab === TABS.INVOICES} onClick={() => setActiveTab(TABS.INVOICES)}>
              {t('invoices')}
            </Tab>
          </TabsHeader>

          {showTimeFilter && (
            <div style={{ marginTop: '1.25rem' }}>
              <TimeFilter value={filter} onChange={setFilter} />
            </div>
          )}

          <TabBody>{renderTab()}</TabBody>
        </>
      )}
    </Area>
  )
}

WalletContent.whyDidYouRender = true

export default memo(WalletContent)
